import { Box, Tooltip } from "@mui/material";

interface BadgeRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface Props {
  messages: string[];
  rect: BadgeRect;
}

const BADGE_SIZE = 18;

/**
 * Small info-icon overlay rendered above + slightly right of a
 * problematic highlight. Positioned in absolute layer coordinates
 * (the same space the lib uses for `.Highlight__part`) so it lands
 * on the annotation rather than at the page origin. Hover reveals
 * the per-highlight diagnostic bullets; replaces the prior
 * colored-outline taxonomy. Uses plain inline style for top/left
 * to avoid MUI sx's theme.spacing surprises with numeric values.
 */
const DiagnosticBadge = ({ messages, rect }: Props) => (
  <Tooltip
    arrow
    placement="top"
    enterDelay={150}
    // The default MUI tooltip body is rgba(97,97,97,0.92), which only
    // marginally clears WCAG AA (~6.3:1 against white) and can fall
    // below threshold over light PDF backgrounds because of the alpha.
    // Force a solid dark grey to keep ~16:1 contrast in any context.
    componentsProps={{
      tooltip: {
        sx: {
          backgroundColor: "#212121",
          color: "#fff",
          fontSize: "0.8125rem",
        },
      },
      arrow: {
        sx: { color: "#212121" },
      },
    }}
    title={
      <Box component="ul" sx={{ m: 0, pl: 2, listStyle: "disc" }}>
        {messages.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </Box>
    }
  >
    <Box
      style={{
        position: "absolute",
        // Nudge up + right so the icon doesn't overlap the start of
        // the highlighted text.
        top: rect.top - BADGE_SIZE - 2,
        left: rect.left + rect.width + 2,
        width: BADGE_SIZE,
        height: BADGE_SIZE,
      }}
      sx={{
        borderRadius: "50%",
        backgroundColor: "#fff",
        // Matches the canvas badge (CanvasDiagnosticLayer): plain
        // white circle, 1.5px black border, italic bold "i" inside.
        border: "1.5px solid #000",
        color: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "serif",
        fontStyle: "italic",
        fontWeight: "bold",
        fontSize: 12,
        lineHeight: 1,
        // Italic "i" sits slightly left of center because the glyph
        // has more visual weight on its right side; nudge the text
        // node one pixel right so it reads centered.
        textIndent: "1px",
        zIndex: 10,
        cursor: "help",
        pointerEvents: "auto",
      }}
    >
      i
    </Box>
  </Tooltip>
);

export default DiagnosticBadge;
